import { inject, Injectable, signal } from "@angular/core";

import { Customer } from "../../../../../models/api/asset/customer";
import { GateFault } from "../../../../../models/api/fault/gateFault";
import { AttributeResponse, TelemetryResponse } from "../../../../../models/shared/telemetry";
import { TranslateService } from "@ngx-translate/core";
import { DateTime } from "luxon";
import { forkJoin, map, Observable } from "rxjs";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { GateFaultService } from "../../../../../services/api/gate-fault/gate-fault.service";
import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { EnergyToolUtilsService } from "../../../../../services/utils/energy-tool-utils/energy-tool-utils.service";
import { GateFaultUtilsService } from "../../../../../services/utils/gate-fault-utils/gate-fault-utils.service";
import { DOCUMENT } from "@angular/common";
import {
  AttributesSubscriptionHandle,
  AttributesWebsocketService,
} from "../../../../../services/websocket/attributes-websocket/attributes-websocket.service";
import {
  GateFaultSubscriptionHandle,
  GateFaultWebsocketService,
} from "../../../../../services/websocket/gate-fault/gate-fault-websocket.service";

const TELEMETRY_KEYS = [
  "readyGateSystems",
  "numberOfGateSystems",
  "openGateSystems",
  "partiallyOpenGateSystems",
  "closedGateSystems",
];

@Injectable()
export class StatisticsService {
  // Services needed:
  private telemetryService = inject(TelemetryService);
  private gateFaultService = inject(GateFaultService);
  private assetService = inject(AssetService);
  private energyToolUtils = inject(EnergyToolUtilsService);
  private translate = inject(TranslateService);
  private gateFaultUtils = inject(GateFaultUtilsService);
  private attributesWebsocket = inject(AttributesWebsocketService);
  private gateFaultWebsocket = inject(GateFaultWebsocketService);
  private attributesSubscription?: AttributesSubscriptionHandle;
  private gateFaultSubscription?: GateFaultSubscriptionHandle;

  private document = inject(DOCUMENT);
  private localStorage = this.document.defaultView?.localStorage;

  // State variables:
  private readonly _gateFaultState = signal<GateFault[] | null>(null);
  readonly gateFaultState = this._gateFaultState.asReadonly();

  private readonly _lineChartEnergyLoss = signal<any>(null);
  readonly lineChartEnergyLoss = this._lineChartEnergyLoss.asReadonly();

  private readonly _openCount = signal<number | null>(null);
  readonly openCount = this._openCount.asReadonly();

  private readonly _partiallyOpenCount = signal<number | null>(null);
  readonly partiallyOpenCount = this._partiallyOpenCount.asReadonly();

  private readonly _closedCount = signal<number | null>(null);
  readonly closedCount = this._closedCount.asReadonly();

  private readonly _availableCount = signal<number | null>(null);
  readonly availableCount = this._availableCount.asReadonly();

  private readonly _totalCount = signal<number | null>(null);
  readonly totalCount = this._totalCount.asReadonly();

  /**
   * Loads initial state of the Statistics page.
   * @param customerId ID of customer to load statistics for.
   */
  public loadInitialState(customerId: string) {
    this.resetStates();

    forkJoin({
      gateFaults: this.fetchGateFaults(),
      attributes: this.fetchAttributes(customerId),
      customer: this.fetchCustomer(customerId),
      energyLoss: this.fetchEnergyLoss(customerId),
      customerGateSystems: this.fetchCustomerGateSystems(customerId),
    }).subscribe({
      next: (res: {
        gateFaults: GateFault[];
        attributes: AttributeResponse;
        customer: Customer;
        energyLoss: TelemetryResponse;
        customerGateSystems: string[];
      }) => {
        const gateFaults = res.gateFaults;
        const attributes = res.attributes;
        const customerGateSystems = res.customerGateSystems;

        this.extractGateFaults(gateFaults);
        if (attributes) {
          this.extractAttributes(attributes);
        }
        this.extractEnergyLossLineChart(res.energyLoss, res.customer);

        this.initRealtimeAttributes(customerId);
        this.initRealtimeGateFaults(customerGateSystems);
      },
    });
  }
  /**
   * Destroys the real-time WebSocket connections.
   * This is used when the user navigates away from the Statistics page.
   */
  public destroy() {
    this.resetStates();
    this.attributesSubscription?.close();
    this.gateFaultSubscription?.close();
  }

  /**
   * Resets all state signals to null.
   * This is used to reset the component's state when the user navigates away.
   */
  private resetStates() {
    this._gateFaultState.set(null);
    this._lineChartEnergyLoss.set(null);
    this._openCount.set(null);
    this._partiallyOpenCount.set(null);
    this._closedCount.set(null);
  }
  /**
   * Initializes real-time gate faults WebSocket connection.
   * @param gateSystemIds gate system IDs to listen for
   */

  private initRealtimeGateFaults(gateSystemIds: string[]) {
    this.gateFaultSubscription?.close();
    const subscription = this.gateFaultWebsocket.register({
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: gateSystemIds,
      lang: this.localStorage?.getItem("lang"),
    });
    this.gateFaultSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        if (incomingData.data) {
          this.gateFaultUtils.handleRealtimeGateFault(
            incomingData.data,
            this._gateFaultState.update.bind(this._gateFaultState),
            true
          );
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Initializes real-time attributes WebSocket connection.
   * @param customerId ID of customer to listen for attributes
   * @param token authorization token
   */
  private initRealtimeAttributes(customerId: string) {
    this.attributesSubscription?.close();
    const subscription = this.attributesWebsocket.register({
      entityType: ASSET_TYPES.CUSTOMER,
      entityId: [customerId],
      keys: TELEMETRY_KEYS,
    });
    this.attributesSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        this.extractAttributes(incomingData.data);
      },
      error: () => undefined,
    });
  }

  /**
   * Extracts energy loss line chart data from telemetry response.
   * @param energyLoss telemetry response
   * @param customer customer that the energy loss belongs to
   */
  private extractEnergyLossLineChart(energyLoss: TelemetryResponse, customer: Customer) {
    const chartData = this.energyToolUtils.convertTimeseriesToChartData(
      energyLoss.energy_loss,
      "day",
      "year",
      false,
      DateTime.now().toMillis(),
      true
    );
    if (customer.expectedEnergyLossPerYear === undefined) {
      return;
    }
    const expectedChartData = this.energyToolUtils.createExpectedChart(customer.expectedEnergyLossPerYear);
    this._lineChartEnergyLoss.set([
      {
        label: this.translate.instant("energy_loss_yearly_chart.line_label"),
        data: chartData,
        borderColor: "#ffffff",
      },
      {
        label: this.translate.instant("energy_loss_yearly_chart.expected_label"),
        data: expectedChartData,
        borderColor: "#ff6347",
      },
    ]);
  }

  /**
   * Extracts gate faults from gate fault response.
   * @param gateFaults gate fault response
   */
  private extractGateFaults(gateFaults: GateFault[]) {
    this._gateFaultState.set(gateFaults);
  }

  /**
   * Extracts attributes from attributes response.
   * @param attributes attributes response
   */
  private extractAttributes(attributes: AttributeResponse) {
    if (attributes.readyGateSystems) {
      this._availableCount.set(attributes.readyGateSystems.value);
    }
    if (attributes.numberOfGateSystems) {
      this._totalCount.set(attributes.numberOfGateSystems.value);
    }
    if (attributes.openGateSystems) {
      this._openCount.set(attributes.openGateSystems.value);
    }
    if (attributes.partiallyOpenGateSystems) {
      this._partiallyOpenCount.set(attributes.partiallyOpenGateSystems.value);
    }
    if (attributes.closedGateSystems) {
      this._closedCount.set(attributes.closedGateSystems.value);
    }
  }

  /**
   * Fetches attributes for a customer.
   * @param customerId ID of customer to fetch attributes for
   */
  private fetchAttributes(customerId: string) {
    return this.telemetryService.getAttributes(customerId, ASSET_TYPES.CUSTOMER, TELEMETRY_KEYS.join(","));
  }

  /**
   * Fetches customer.
   * @param customerId ID of customer to fetch
   */
  private fetchCustomer(customerId: string) {
    return this.assetService.getAssetDetails(customerId, ASSET_TYPES.CUSTOMER).pipe(
      map((res) => {
        return res as Customer;
      })
    );
  }

  /**
   * Fetches gate faults.
   */
  private fetchGateFaults() {
    return this.gateFaultService.getGateFaults();
  }

  /**
   * Fetches energy loss for a customer.
   * @param customerId ID of customer to fetch energy loss for
   */
  private fetchEnergyLoss(customerId: string) {
    const now = DateTime.now();

    return this.telemetryService.getTimeseries(
      customerId,
      ASSET_TYPES.CUSTOMER,
      "energy_loss",
      now.startOf("year").toMillis(),
      now.toMillis()
    );
  }

  /**
   * Fetches customer gate systems.
   * @param customerId ID of customer to fetch gate systems for
   */
  private fetchCustomerGateSystems(customerId: string): Observable<string[]> {
    return this.assetService.getAssets(ASSET_TYPES.GATE_SYSTEM, customerId, ASSET_TYPES.CUSTOMER).pipe(
      map((res) => {
        const gateSystemIds: string[] = [];
        res.forEach((gateSystem) => {
          if (gateSystem && gateSystem.id) {
            gateSystemIds.push(gateSystem.id);
          }
        });
        return gateSystemIds;
      })
    );
  }
}
